/**
 * The assistant's session across Helm: which conversation is open, whether the
 * floating panel is showing, and which page the person is on. One session per
 * signed-in tab, shared by the widget and the full-page assistant, so moving
 * between pages never starts a second conversation or a second call.
 *
 * Kept free of React and of the app's path aliases so it can be tested on its
 * own.
 */

export type PageKind = 'inquiry' | 'consignment' | 'customer'

/** The page the person has open, as the assistant is told about it. */
export interface PageContext {
  /** The route, without query or hash: `/inquiries/<id>`. */
  path: string
  /** Set on a record's own page. */
  kind: PageKind | null
  id: string | null
}

const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}'

const RECORD_PAGES: Array<[PageKind, RegExp]> = [
  ['inquiry', new RegExp(`^/inquiries/(${UUID})(?:/|$)`, 'i')],
  ['consignment', new RegExp(`^/consignments/(${UUID})(?:/|$)`, 'i')],
  ['customer', new RegExp(`^/customers/(${UUID})(?:/|$)`, 'i')],
]

/**
 * Reads the page from the route alone. Only the kind and id travel: the
 * assistant reads the record itself, with the person's own access, so naming a
 * page never shows it anything they could not open.
 */
export function pageContextFor(pathname: string): PageContext {
  const path = pathname.split(/[?#]/)[0] || '/'
  for (const [kind, pattern] of RECORD_PAGES) {
    const match = pattern.exec(path)
    if (match?.[1]) return { path, kind, id: match[1].toLowerCase() }
  }
  return { path, kind: null, id: null }
}

/** Whether two pages are the same record (or the same non-record route). */
export function samePage(a: PageContext | null, b: PageContext | null): boolean {
  if (!a || !b) return a === b
  return a.kind && b.kind ? a.kind === b.kind && a.id === b.id : a.path === b.path
}

/** The full-page assistant already shows the conversation, so the widget steps aside there. */
export function widgetHiddenOn(pathname: string): boolean {
  return pathname === '/assistant' || pathname.startsWith('/assistant/')
}

export interface AssistantSessionState {
  /** The open conversation; null for a new one not yet started. */
  threadId: string | null
  /** The floating panel (a sheet on phones). Closing it ends nothing. */
  panelOpen: boolean
  page: PageContext | null
}

export type AssistantSessionAction =
  | { type: 'panel.opened' }
  | { type: 'panel.closed' }
  | { type: 'thread.selected'; threadId: string | null }
  | { type: 'page.changed'; page: PageContext }

export const initialAssistantSession: AssistantSessionState = {
  threadId: null,
  panelOpen: false,
  page: null,
}

/**
 * How the session moves. Navigating changes only the page: the conversation,
 * the panel and any call carry on. Only choosing another conversation (or a
 * new one) changes which thread is open.
 */
export function assistantSessionReducer(
  state: AssistantSessionState,
  action: AssistantSessionAction,
): AssistantSessionState {
  switch (action.type) {
    case 'panel.opened':
      return state.panelOpen ? state : { ...state, panelOpen: true }
    case 'panel.closed':
      return state.panelOpen ? { ...state, panelOpen: false } : state
    case 'thread.selected':
      return state.threadId === action.threadId ? state : { ...state, threadId: action.threadId }
    case 'page.changed':
      return samePage(state.page, action.page) && state.page?.path === action.page.path
        ? state
        : { ...state, page: action.page }
    default:
      return state
  }
}

/** The tool Beacon drafts client replies with; its result becomes an approval card. */
export const DRAFT_REPLY_TOOL = 'draft_client_reply'

/** A reply Beacon was asked to draft, as its card needs it. */
export interface ReplyDraftRequest {
  /** The background job writing the draft; one card per job. */
  jobId: string
  inquiryId: string | null
}

interface ToolCallLike {
  toolName: string
  status: string
  result?: unknown
}

/** The draft a tool call asked for, if it is a finished draft request. */
export function replyDraftRequest(call: ToolCallLike): ReplyDraftRequest | null {
  if (call.toolName !== DRAFT_REPLY_TOOL || call.status !== 'completed') return null
  const result = call.result as { job_id?: unknown; inquiry_id?: unknown } | null | undefined
  if (!result || typeof result.job_id !== 'string' || !result.job_id) return null
  return {
    jobId: result.job_id,
    inquiryId: typeof result.inquiry_id === 'string' ? result.inquiry_id : null,
  }
}

/**
 * Adds the draft requests the server kept that the screen is not showing yet,
 * such as one made during a call, without repeating one already streamed in
 * (which carries the model's call id rather than the kept record's).
 */
export function mergeKeptCalls<T extends ToolCallLike>(local: T[], server: T[]): T[] {
  const shown = new Set(local.map((call) => replyDraftRequest(call)?.jobId).filter(Boolean))
  const missing: T[] = []
  for (const call of server) {
    const request = replyDraftRequest(call)
    if (!request || shown.has(request.jobId)) continue
    shown.add(request.jobId)
    missing.push(call)
  }
  return missing.length ? [...local, ...missing] : local
}

/**
 * Whether a conversation has nothing to show yet: none is open, or the one this
 * session just started (a file attached, a call under way) has no message,
 * reply, card or change in it. The full page shows its home for a blank one;
 * the first thing said or proposed moves it to the conversation. A past
 * conversation someone opens is never blank, even while it loads.
 */
export function isBlankConversation(state: {
  threadId: string | null
  startedThread: string | null
  messages: number
  streaming: boolean
  confirmations: number
  toolCalls: number
  /** Actions Beacon proposed, e.g. during a call started on the home. */
  proposals?: number
}): boolean {
  return (
    (!state.threadId || state.threadId === state.startedThread) &&
    state.messages === 0 &&
    !state.streaming &&
    state.confirmations === 0 &&
    state.toolCalls === 0 &&
    !state.proposals
  )
}

interface ProposalLike {
  id: string
  batch_id: string
  position: number
  run_id: string | null
}

/**
 * Proposals as cards: those made together in one, in the order they were
 * proposed, each card placed with the run that made it.
 */
export function proposalBatches<T extends ProposalLike>(
  proposals: T[],
): Array<{ batchId: string; runId: string | null; proposals: T[] }> {
  const batches = new Map<string, { batchId: string; runId: string | null; proposals: T[] }>()
  for (const proposal of proposals) {
    const batch = batches.get(proposal.batch_id) ?? {
      batchId: proposal.batch_id,
      runId: proposal.run_id,
      proposals: [],
    }
    batch.proposals.push(proposal)
    batches.set(proposal.batch_id, batch)
  }
  for (const batch of batches.values()) batch.proposals.sort((a, b) => a.position - b.position)
  return [...batches.values()]
}

/** Newer copies of proposals replace older ones; new ones are added at the end. */
export function mergeProposals<T extends { id: string }>(current: T[], incoming: T[]): T[] {
  if (incoming.length === 0) return current
  const byId = new Map(incoming.map((proposal) => [proposal.id, proposal]))
  const merged = current.map((proposal) => byId.get(proposal.id) ?? proposal)
  const known = new Set(current.map((proposal) => proposal.id))
  return [...merged, ...incoming.filter((proposal) => !known.has(proposal.id))]
}
